import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Collapse,
    Grid,
    IconButton,
    Paper,
    Typography,
} from '@mui/material'
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Description as DescriptionIcon,
} from '@mui/icons-material'
import { useAppStore, useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import {
    getAvailableContractVersions,
    ContractVersionInfo,
    ContractVersionsResponse,
} from '@assembly/api/contracts'
import ContractViewerModal from './ContractViewerModal'

export default function Contracts() {
    const { setAppBarTitle } = useAppStore()
    const { addAlert } = useSnackbars()

    const [versions, setVersions] = useState<ContractVersionsResponse>({})
    const [loading, setLoading] = useState(true)
    const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set())
    const [viewModalOpen, setViewModalOpen] = useState(false)
    const [selectedCountry, setSelectedCountry] = useState<string>('')
    const [selectedVersion, setSelectedVersion] = useState<string>('')

    useEffect(() => {
        setAppBarTitle('Contracts')
        fetchContractVersions()
    }, [])

    const fetchContractVersions = async () => {
        try {
            setLoading(true)
            const response = await getAvailableContractVersions()
            setVersions(response.data)
        } catch (error: any) {
            const errorMessage =
                error?.response?.data?.message || 'Failed to fetch contract versions'
            addAlert({ message: errorMessage, type: SnackbarType.Error })
        } finally {
            setLoading(false)
        }
    }

    const handleToggleVersion = (versionCode: string) => {
        const newExpanded = new Set(expandedVersions)
        if (newExpanded.has(versionCode)) {
            newExpanded.delete(versionCode)
        } else {
            newExpanded.add(versionCode)
        }
        setExpandedVersions(newExpanded)
    }

    const handleViewContract = (versionCode: string, countryCode: string) => {
        setSelectedVersion(versionCode)
        setSelectedCountry(countryCode)
        setViewModalOpen(true)
    }

    const handleCloseModal = () => {
        setViewModalOpen(false)
        setSelectedVersion('')
        setSelectedCountry('')
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        )
    }

    const versionEntries = Object.entries(versions).sort(([a], [b]) => {
        // Sort versions: v0, v1, v1-internal, v1.1, v1.1-internal, etc.
        return a.localeCompare(b)
    })

    return (
        <Box>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Contract Versions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Click on a contract version to view available countries. Click "View Contract"
                    to see the contract HTML for a specific country and version.
                </Typography>

                {versionEntries.length === 0 ? (
                    <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                        No contract versions available
                    </Typography>
                ) : (
                    <Grid container spacing={2}>
                        {versionEntries.map(([versionCode, versionInfo]) => {
                            const isExpanded = expandedVersions.has(versionCode)
                            const countries = versionInfo.availableCountries.sort()

                            return (
                                <Grid item xs={12} key={versionCode}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Box display="flex" alignItems="center" gap={2} flex={1}>
                                                    <DescriptionIcon color="primary" />
                                                    <Box flex={1}>
                                                        <Typography variant="h6">
                                                            {versionCode}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            {versionInfo.description}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ mt: 0.5, display: 'block' }}
                                                        >
                                                            {countries.length}{' '}
                                                            {countries.length === 1 ? 'country' : 'countries'}{' '}
                                                            available
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <IconButton
                                                    onClick={() => handleToggleVersion(versionCode)}
                                                    aria-label={
                                                        isExpanded ? 'collapse' : 'expand'
                                                    }
                                                >
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                </IconButton>
                                            </Box>

                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                                                    {countries.length === 0 ? (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ py: 2 }}
                                                        >
                                                            No countries available for this version
                                                        </Typography>
                                                    ) : (
                                                        <Grid container spacing={1}>
                                                            {countries.map((countryCode) => (
                                                                <Grid item key={countryCode}>
                                                                    <Button
                                                                        variant="outlined"
                                                                        size="small"
                                                                        onClick={() =>
                                                                            handleViewContract(
                                                                                versionCode,
                                                                                countryCode
                                                                            )
                                                                        }
                                                                        sx={{ textTransform: 'none' }}
                                                                    >
                                                                        {countryCode} - View Contract
                                                                    </Button>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )
                        })}
                    </Grid>
                )}
            </Paper>

            <ContractViewerModal
                open={viewModalOpen}
                onClose={handleCloseModal}
                versionCode={selectedVersion}
                countryCode={selectedCountry}
            />
        </Box>
    )
}

